import React, { useState } from 'react';

interface FAQ {
  question: string;
  answer: string;
  category: 'conversion' | 'product' | 'pricing';
}

const EnhancedFAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: 'How does Explanatum replace tools like HubSpot, Asana, and Zapier?',
      answer: 'Explanatum provides native modules for CRM (replaces HubSpot/Salesforce), Project Management (replaces Asana/Monday.com), and built-in automation between all modules (replaces Zapier). Unlike buying separate tools, all our modules share data automatically, eliminating the need for third-party integrations.',
      category: 'conversion',
    },
    {
      question: 'What happens after my 7-day free trial ends?',
      answer: 'After 7 days, your payment method will be charged £19/month for the Premium plan or £49/month for the Team plan. You can cancel anytime during the trial with zero charges. No contracts, no commitments — just pay monthly and cancel whenever you want.',
      category: 'pricing',
    },
    {
      question: 'Can I import my existing data from other tools?',
      answer: 'Yes! We support CSV imports for CRM contacts, companies, and deals. You can also manually migrate data or use our AI assistant to help structure and import your information. Most customers complete their migration in under 2 hours.',
      category: 'conversion',
    },
    {
      question: 'Do all 23 modules work together or are they separate tools?',
      answer: 'All modules are deeply interconnected. For example, your CRM data feeds into your email campaigns, which connect to your project management tasks, all while the AI Consultant monitors everything and provides insights. This is what makes Explanatum 10x more powerful than buying separate tools.',
      category: 'product',
    },
    {
      question: 'Is there a setup fee or implementation cost?',
      answer: 'Absolutely not. There are zero setup fees, zero implementation costs, and zero hidden charges. Pay £19/month for Premium or £49/month for Team — that\'s it. You get access to everything immediately after signing up.',
      category: 'pricing',
    },
    {
      question: 'What if I only need a few modules? Do I have to pay for all 23?',
      answer: 'Yes, all plans include all 23 modules plus 40+ additional tools. We don\'t offer à la carte pricing because the real power comes from the interconnected ecosystem. However, at £19/month, it\'s still cheaper than buying even 2-3 individual SaaS tools.',
      category: 'pricing',
    },
    {
      question: 'How quickly can I get started and see results?',
      answer: 'You can create your account and start using modules within 2 minutes. Most businesses see immediate value from the AI Business Consultant, CRM, and content generation tools on day one. The more you use the platform, the smarter it gets.',
      category: 'conversion',
    },
    {
      question: 'What kind of support do you offer?',
      answer: 'Premium users get email and in-app chat support. Team plan users get priority support with faster response times. We also have extensive documentation, video tutorials, and an AI assistant built into the platform to help answer questions instantly.',
      category: 'product',
    },
    {
      question: 'Is my data secure and private?',
      answer: 'Absolutely. We use bank-level encryption, are GDPR compliant, and are SOC 2 certified. Your data is stored in secure, EU-based data centers with 99.9% uptime. We never sell or share your data with third parties.',
      category: 'product',
    },
    {
      question: 'Can I switch from monthly to annual billing later?',
      answer: 'Yes! You can upgrade from monthly to annual billing at any time from your account settings. When you switch to annual, you\'ll immediately save 20% (Premium) or 17% (Team) on your subscription.',
      category: 'pricing',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold text-purple-600 bg-purple-50 rounded-full">
            Got Questions?
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about Explanatum
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border-2 border-gray-100"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-blue-50 transition-colors"
                >
                  <span className="font-bold text-gray-900 text-lg pr-8">
                    {faq.question}
                  </span>
                  <svg
                    className={`w-6 h-6 text-blue-600 transform transition-transform duration-300 flex-shrink-0 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96 pb-5' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Still have questions CTA */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border-2 border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-4">
              Chat with our AI assistant or contact our support team
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:support@explanatum.co.uk"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:shadow-lg transition-all"
              >
                Contact Support
              </a>
              <button
                onClick={() => {
                  // This would trigger the product assistant widget
                  const event = new CustomEvent('openProductAssistant');
                  window.dispatchEvent(event);
                }}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-all"
              >
                Ask AI Assistant
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnhancedFAQSection;
